import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import type { RootState } from '@/app/store';
import { setEnvAuth, clearEnvAuth, type AdminUser } from '@/features/auth/authSlice';
import { getApiUrl } from '@/shared/runtimeConfig';

const mutex = new Mutex();

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const state = api.getState() as RootState;
  const env = state.auth.currentEnv;
  const apiUrl = getApiUrl(env);
  const token = state.auth.byEnv[env]?.accessToken;

  const raw = fetchBaseQuery({
    baseUrl: `${apiUrl}/api/v1/admin`,
    credentials: 'include',
    prepareHeaders: (headers) => {
      if (token) headers.set('Authorization', `Bearer ${token}`);
      return headers;
    },
  });

  return raw(args, api, extraOptions);
};

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  await mutex.waitForUnlock();
  let result = await dynamicBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const state = api.getState() as RootState;
        const env = state.auth.currentEnv;
        const refresh = await dynamicBaseQuery(
          { url: '/auth/refresh', method: 'POST' },
          api,
          extraOptions,
        );
        if (refresh.data) {
          const data = (
            refresh.data as {
              success?: boolean;
              data?: { accessToken: string; user: AdminUser };
            }
          ).data;
          if (data?.accessToken) {
            api.dispatch(
              setEnvAuth({
                env,
                payload: { accessToken: data.accessToken, user: data.user },
              }),
            );
            result = await dynamicBaseQuery(args, api, extraOptions);
          } else {
            api.dispatch(clearEnvAuth(env));
          }
        } else {
          api.dispatch(clearEnvAuth(env));
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await dynamicBaseQuery(args, api, extraOptions);
    }
  } else if (result.error?.status === 403) {
    const code = (result.error.data as { error?: { code?: string } } | undefined)?.error?.code;
    if (code === 'IP_NOT_ALLOWED') window.location.assign('/ip-blocked');
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'AdminUser',
    'AdminProject',
    'AdminEstimate',
    'AdminMaterial',
    'AdminRate',
    'AdminChat',
    'AdminTask',
    'AdminTeam',
    'AdminNotification',
    'AdminAuditLog',
    'AdminSession',
    'AdminMetrics',
    'EditMode',
    'AdminBackup',
    'AdminHealth',
    'AdminFeatureFlag',
    'AdminLemana',
    'AdminPushSubscription',
    'AdminErrorGroup',
    'AdminErrorEvent',
  ],
  endpoints: () => ({}),
});

export const adminApi = baseApi;
